import React, { useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Formik, Form, Field } from "formik";
import {
    PostRtoCityLocation,
    UpdateRtoCityLocation,
    UpdateUser,
    GetZone,
} from "../../../Service/_index";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import { ToastError } from "../../../UiComponents/Toaster/Toast";
import Styles from "../../../UiComponents/Scss/AddPopup.module.scss";
import { Switch, FormControlLabel, Autocomplete, TextField } from "@mui/material";

/**
 * Display-only label for the row being typed: "CITY" on its own, or
 * "CITY - REGION" once a region is filled in. Nothing here is submitted —
 * it just shows the user how the pair reads together.
 */
const previewLabel = (values) => {
    const city = (values.city || "").toUpperCase().trim();
    const region = (values.Region || "").toUpperCase().trim();
    if (!city) return "";
    return region ? `${city} - ${region}` : city;
};

const AddRTOCityLocation = (props) => {
    const {
        selectedData,
        formType,
        setOpenDrawer,
        title,
        GetData,
        // Set for non-admin users: after saving, link the row to their own
        // User.rtocityids so it appears in their Online Policy dropdown.
        mapToUser = false,
        userRtoRows = [],
    } = props;
    let formRef = useRef();

    // The schema field is `isActive` — the form previously posted `isEnabled`,
    // which Mongoose strict mode dropped, so the switch never took effect.
    const initialValues = {
        RTOCode: "",
        Region: "",
        city: "",
        // Reference to a Zone row — the rate lives there, not here, so editing
        // it on the Zone master applies to every RTO pointing at this zone.
        zoneId: "",
        isActive: true,
    };

    const UserId = sessionStorage.getItem("UserId");

    // Zones for the picker. Only active ones are offered, but a policy already
    // pointing at a retired zone keeps showing it (see selectedZone below).
    const [zoneList, setZoneList] = React.useState([]);
    React.useEffect(() => {
        let cancelled = false;
        GetZone()
            .then((res) => {
                if (cancelled) return;
                const rows = Array.isArray(res?.data) ? res.data : [];
                setZoneList(rows.filter((z) => z.isActive !== false));
            })
            .catch(() => {
                if (!cancelled) setZoneList([]);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (formType === "edit") {
            const editValues = {
                RTOCode: selectedData.RTOCode || "",
                Region: selectedData.Region || "",
                city: selectedData.city || "",
                zoneId: selectedData.zoneId || "",
                isActive: selectedData.isActive !== undefined ? selectedData.isActive : true,
            };
            formRef.setValues(editValues);
        }
    }, [formType, selectedData]);

    /**
     * Append the saved row to this user's assignments.
     *
     * The backend returns the EXISTING master row when the RTO code is already
     * present, so this also covers "I typed a code somebody else already
     * created" — it links that row instead of creating a duplicate.
     */
    const linkToUser = async (savedRow) => {
        const rowId = savedRow?._id;
        if (!mapToUser || !rowId || !UserId) return;

        const current = Array.isArray(userRtoRows) ? userRtoRows : [];
        if (current.some((entry) => String(entry.rtoCity) === String(rowId))) {
            return; // already assigned — nothing to save
        }

        const updated = [
            ...current.map((entry) => ({ rtoCity: entry.rtoCity })),
            { rtoCity: rowId },
        ];

        // Same transport the user form uses: multipart FormData with the array
        // stringified; dao/userDao.js putUser parses it back.
        const formData = new FormData();
        formData.append("rtocityids", JSON.stringify(updated));
        await UpdateUser(UserId, formData);
    };

    const onKeyDownHandler = (e, values) => {
        if (e.key === "Enter") {
            e.preventDefault();
            onSubmit(e, values, "Save");
        }
    };

    const onSubmit = (e, data, keys) => {
        if (e && e.preventDefault) e.preventDefault();

        // Normalize data if needed (e.g. uppercase)
        const processedData = { ...data };
        ["RTOCode", "Region", "city"].forEach(
            (item) => (processedData[item] = processedData[item]?.toUpperCase()?.trim())
        );

        // Store RTOCode as XX00 (e.g., TN01) without hyphen in Master
        if (processedData.RTOCode) {
            processedData.RTOCode = processedData.RTOCode.replace(/[^a-zA-Z0-9]/g, "");
        }

        processedData.updatedAt = new Date();

        if (keys === "Save" || (e && e.key === "Enter")) {
            if (processedData.RTOCode && processedData.city) {
                if (formType === "add") {
                    processedData.createdBy = UserId;
                } else if (formType === "edit") {
                    processedData.updatedBy = UserId;
                }

                let SubmitType =
                    formType === "edit"
                        ? UpdateRtoCityLocation(selectedData._id, processedData)
                        : PostRtoCityLocation(processedData);

                SubmitType.then(async (res) => {
                    if (res) {
                        if (formType === "add") {
                            await linkToUser(res.data);
                        }
                        setOpenDrawer(false);
                        GetData();
                    }
                }).catch((error) => {
                    console.log(error);
                });
            } else {
                ToastError("Please fill all required fields!");
            }
        } else if (keys === "Save&Create") {
            if (processedData.RTOCode && processedData.city) {
                PostRtoCityLocation(processedData).then(async (res) => {
                    if (res) {
                        await linkToUser(res.data);
                        formRef.resetForm();
                        GetData();
                    }
                });
            } else {
                ToastError("Please fill all required fields!");
            }
        }
    };

    return (
        <div className="DrawerContainer">
            <Grid container className="DrawerHeader">
                <Grid item xs={6} sm={6}>
                    <Typography>{title} RTO City Location</Typography>
                </Grid>
                <Grid item xs={6} sm={6} className="d-flex justify-content-end">
                    <CloseIcon
                        onClick={() => setOpenDrawer(false)}
                        sx={{ cursor: "pointer" }}
                    />
                </Grid>
            </Grid>
            <Formik
                initialValues={initialValues}
                onSubmit={(values) => {
                    onSubmit(null, values, "Save");
                }}
                innerRef={(ref) => {
                    if (ref) {
                        formRef = ref;
                    }
                }}
            >
                {({ values, setFieldValue }) => (
                    <Form onKeyDown={(e) => onKeyDownHandler(e, values)}>
                        <div className={Styles.addContent}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography
                                        className={Styles.inputTitle}
                                        sx={{ fontSize: "12px", mb: 1 }}
                                    >
                                        RTO Code *
                                    </Typography>
                                    <Field
                                        name="RTOCode"
                                        placeholder="Enter RTO Code"
                                        className="InputFiled"
                                        style={{ textTransform: "uppercase" }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography
                                        className={Styles.inputTitle}
                                        sx={{ fontSize: "12px", mb: 1 }}
                                    >
                                        Zone
                                    </Typography>
                                    <Autocomplete
                                        className="AutoComplete_InputBox"
                                        disablePortal
                                        options={zoneList}
                                        value={
                                            zoneList.find(
                                                (z) => String(z._id) === String(values.zoneId)
                                            ) || null
                                        }
                                        onChange={(e, v) =>
                                            setFieldValue("zoneId", v?._id || "")
                                        }
                                        getOptionLabel={(option) =>
                                            option?.zoneName || ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            String(option?._id) === String(value?._id)
                                        }
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Select Zone" />
                                        )}
                                    />
                                    <Typography sx={{ fontSize: "11px", color: "#667085", mt: 0.5 }}>
                                        Add or edit zones with the Zone button on the list page.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography
                                        className={Styles.inputTitle}
                                        sx={{ fontSize: "12px", mb: 1 }}
                                    >
                                        City *
                                    </Typography>
                                    <Field
                                        name="city"
                                        placeholder="Enter City"
                                        className="InputFiled"
                                        style={{ textTransform: "uppercase" }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography
                                        className={Styles.inputTitle}
                                        sx={{ fontSize: "12px", mb: 1 }}
                                    >
                                        Region
                                    </Typography>
                                    <Field
                                        name="Region"
                                        placeholder="Enter Region"
                                        className="InputFiled"
                                        style={{ textTransform: "uppercase" }}
                                    />
                                </Grid>
                                {previewLabel(values) && (
                                    <Grid item xs={12} sm={12}>
                                        <Typography
                                            className={Styles.inputTitle}
                                            sx={{ fontSize: "12px", mb: 0.5 }}
                                        >
                                            Preview
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 600,
                                                color: "#101828",
                                                background: "#F9FAFB",
                                                border: "1px solid #EAECF0",
                                                borderRadius: "6px",
                                                padding: "8px 12px",
                                            }}
                                        >
                                            {previewLabel(values)}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={values.isActive}
                                                onChange={(e) => setFieldValue("isActive", e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Enabled"
                                    />
                                </Grid>
                            </Grid>
                        </div>
                        <div className="Dialog_Footers">
                            <div className="Dialog_Button_Groups">
                                <Button
                                    onClick={() => setOpenDrawer(false)}
                                    className="Dialog_Cancels"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    onClick={(e) => onSubmit(e, values, "Save")}
                                    className="Dialog_Saves"
                                >
                                    {formType === "edit" ? "Update" : "Save & Exit"}
                                </Button>
                            </div>
                            {formType === "add" && (
                                <Button
                                    className="Dialog_Save_Exits"
                                    sx={{ marginLeft: "10px" }}
                                    onClick={(e) => onSubmit(e, values, "Save&Create")}
                                >
                                    Save & Create New
                                </Button>
                            )}
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default AddRTOCityLocation;
